import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Input from "./Input";
import Button from "./Button";
import { Panel } from "./record";
import { postReq, getApiBreadcrumbs, ApiBreadcrumb } from "../utils/request";
import { getRecentClientErrors, RecentClientError } from "../utils/logShipper";
import { getClientSessionId } from "../utils/session";
import { BugReportSeverity } from "../types/BugReport";

const SEVERITY_OPTIONS = [
    { value: BugReportSeverity.Low, label: "Low — cosmetic or minor annoyance" },
    { value: BugReportSeverity.Medium, label: "Medium — works, but wrong or awkward" },
    { value: BugReportSeverity.High, label: "High — blocks a task, no workaround" },
    { value: BugReportSeverity.Critical, label: "Critical — data loss or system unusable" },
];

interface CapturedContext {
    screenshotDataUrl: string | null;
    breadcrumbs: ApiBreadcrumb[];
    recentErrors: RecentClientError[];
    route: string;
}

/** The event the report dialog listens for (sidebar footer, account menu, phone drawer). */
export const OPEN_BUG_REPORT_EVENT = "crx:open-bug-report";

/** Open the Report-a-bug dialog from anywhere. */
export const openBugReport = (): void => {
    window.dispatchEvent(new CustomEvent(OPEN_BUG_REPORT_EVENT));
};

/**
 * True while a SideDrawer is open. Drawers portal straight into <body> and their
 * backdrop covers the sidebar, so the floating button returns for that case.
 */
const useSideDrawerOpen = (): boolean => {
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        // Only a drawer WITH a backdrop covers the sidebar's "Report a bug"; a
        // preview panel without one (Quick look) leaves it reachable.
        const check = () => setIsOpen(!!document.querySelector("body > .side-drawer-backdrop"));
        check();
        const observer = new MutationObserver(check);
        observer.observe(document.body, { childList: true });
        return () => observer.disconnect();
    }, []);
    return isOpen;
};

/**
 * Report-a-bug dialog, mounted once on every authenticated page. Opened by
 * openBugReport() from the shell, or by its floating button while a side
 * drawer is open.
 *
 * The value here is the *context*, not the text box: the report carries the
 * route, the browser, the last API calls this tab made, any client errors that
 * fired just beforehand, and a screenshot — so a tester never has to reproduce
 * a bug or remember what they clicked.
 *
 * The screenshot is captured on button click, BEFORE the dialog opens,
 * otherwise the dialog itself would be the picture. Because a screenshot can
 * contain client data, the reporter sees a preview and can drop it with one
 * click.
 */
const BugReportButton: React.FC = () => {
    const isDrawerOpen = useSideDrawerOpen();
    const [showModal, setShowModal] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const isCapturingRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [severity, setSeverity] = useState<string>(BugReportSeverity.Medium);
    const [attachScreenshot, setAttachScreenshot] = useState(true);
    const [context, setContext] = useState<CapturedContext | null>(null);

    /**
     * Grab the visible viewport. A screenshot is a bonus, never a blocker — if
     * html2canvas throws (tainted canvas, cross-origin image), we still open
     * the dialog and file the report without one.
     */
    const captureScreenshot = async (): Promise<string | null> => {
        try {
            // html2canvas is only needed at capture time — loading it on demand
            // keeps it out of the main bundle (this button mounts on every page).
            const { default: html2canvas } = await import("html2canvas");
            const canvas = await html2canvas(document.body, {
                scale: 0.6,
                logging: false,
                useCORS: true,
                allowTaint: true,
                x: window.scrollX,
                y: window.scrollY,
                width: window.innerWidth,
                height: window.innerHeight,
            });
            return canvas.toDataURL("image/jpeg", 0.7);
        } catch {
            return null;
        }
    };

    const handleOpen = async () => {
        if (isCapturingRef.current) return;
        isCapturingRef.current = true;
        setIsCapturing(true);
        const screenshotDataUrl = await captureScreenshot();
        setContext({
            screenshotDataUrl,
            breadcrumbs: getApiBreadcrumbs(),
            recentErrors: getRecentClientErrors(),
            route: window.location.pathname + window.location.search,
        });
        setAttachScreenshot(!!screenshotDataUrl);
        setIsCapturing(false);
        isCapturingRef.current = false;
        setShowModal(true);
    };

    // The shell's "Report a bug" entries dispatch this event. A ref keeps the
    // listener pointing at the latest handleOpen without re-subscribing.
    const handleOpenRef = useRef(handleOpen);
    handleOpenRef.current = handleOpen;
    useEffect(() => {
        const onOpen = () => void handleOpenRef.current();
        window.addEventListener(OPEN_BUG_REPORT_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_BUG_REPORT_EVENT, onOpen);
    }, []);

    const handleClose = () => {
        setShowModal(false);
        setTitle("");
        setDescription("");
        setSeverity(BugReportSeverity.Medium);
        setContext(null);
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            toast.error("Please give the problem a short title");
            return;
        }

        setIsSubmitting(true);
        const response = await postReq("bug-reports", {
            title: title.trim(),
            description: description.trim(),
            severity,
            route: context?.route,
            userAgent: navigator.userAgent,
            appVersion: process.env.REACT_APP_VERSION || "dev",
            // Links this report to every error log line from the same tab.
            correlationId: getClientSessionId(),
            screenshotDataUrl: attachScreenshot ? context?.screenshotDataUrl ?? undefined : undefined,
            breadcrumbs: context?.breadcrumbs ?? [],
            recentErrors: context?.recentErrors ?? [],
        });
        setIsSubmitting(false);

        if (response.status === 201 || response.status === 200) {
            toast.success("Thanks — the report was filed with the page context attached");
            handleClose();
        } else {
            toast.error("Could not file the report. Please try again.");
        }
    };

    const errorCount = context?.recentErrors.length ?? 0;
    const callCount = context?.breadcrumbs.length ?? 0;
    // A failed API call is not a JavaScript error, so it never reaches
    // recentErrors. Counting it separately stops the dialog cheerfully
    // reporting "no errors" while the tab has been throwing 500s.
    const failedCallCount = context?.breadcrumbs.filter(c => c.status === 0 || c.status >= 400).length ?? 0;

    return (
        <>
            {/* Only while a side drawer is open: its backdrop covers the sidebar's
                "Report a bug", and a problem seen inside a drawer should be
                reportable without closing it. A labelled pill, like the chip it stands in for. */}
            {isDrawerOpen && (
                <div
                    data-html2canvas-ignore="true"
                    // Above the side drawer (1080/1081), below Bootstrap
                    // modals (1085/1090) so its own report modal — and any
                    // confirm dialog — still covers it.
                    style={{ position: "fixed", left: 16, bottom: 16, zIndex: 1082 }}
                >
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        icon="bug"
                        loading={isCapturing}
                        loadingText="Capturing…"
                        onClick={handleOpen}
                        title="Report a problem with this page"
                    >
                        Report a bug
                    </Button>
                </div>
            )}

            <Modal show={showModal} onHide={handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-bug me-2" />
                        Report a problem
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="col-12">
                            <Input
                                type="text"
                                label="What went wrong?"
                                id="title"
                                value={title}
                                onChange={(_name, value) => setTitle(value as string)}
                                required
                                icon="bi bi-card-heading"
                            />
                        </div>
                        <div className="col-12">
                            <Input
                                type="textarea"
                                label="What were you doing, and what did you expect?"
                                id="description"
                                value={description}
                                onChange={(_name, value) => setDescription(value as string)}
                                icon="bi bi-chat-left-text"
                            />
                        </div>
                        <div className="col-12">
                            <Input
                                type="select"
                                label="How bad is it?"
                                id="severity"
                                value={severity}
                                onChange={(_name, value) => setSeverity(value as string)}
                                options={SEVERITY_OPTIONS}
                                icon="bi bi-thermometer-half"
                            />
                        </div>
                    </div>

                    {/* Be explicit about what leaves the browser — a screenshot can
                        contain client data, so opting out must be one click. */}
                    <Panel title="Attached automatically" summary="What leaves your browser with this report">
                        <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                            <li>The page you were on (<span style={{ fontFamily: "var(--font-mono)" }}>{context?.route}</span>)</li>
                            <li>Your browser and the app version</li>
                            <li>
                                {callCount} recent API {callCount === 1 ? "call" : "calls"} from this tab
                                {failedCallCount > 0 && (
                                    <strong> — {failedCallCount} of them failed</strong>
                                )}
                            </li>
                            <li>
                                {errorCount} recent client {errorCount === 1 ? "error" : "errors"}
                                {errorCount === 0 && failedCallCount === 0 ? " (none — that's a good sign)" : ""}
                            </li>
                        </ul>

                        {context?.screenshotDataUrl ? (
                            <>
                                <Input
                                    type="checkbox"
                                    label="Attach a screenshot of this page"
                                    id="attachScreenshot"
                                    value={attachScreenshot}
                                    onChange={(_name, value) => setAttachScreenshot(value as boolean)}
                                />
                                {attachScreenshot && (
                                    <img
                                        src={context.screenshotDataUrl}
                                        alt="Screenshot that will be attached to this report"
                                        style={{
                                            maxWidth: "100%",
                                            border: "1px solid var(--surface-border)",
                                            borderRadius: 4,
                                            marginTop: 8,
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <div style={{ opacity: 0.7 }}>
                                <i className="bi bi-image-alt me-2" />
                                A screenshot could not be captured on this page — the report will be filed without one.
                            </div>
                        )}
                    </Panel>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="ghost" icon="x-lg" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="button" variant="primary" icon="send" onClick={handleSubmit} loading={isSubmitting} loadingText="Sending…">
                        Send report
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BugReportButton;
